import { Button, Input } from "antd";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { CardPaymentForm } from "../../components/card-payment.form";
import { COUNTRY_CODE, plans } from "../../utils/constants";
import { toFixed, toFixedDisplay } from "../../utils/number.utils";
import { ICardDetails } from "../../types/types";
import { useCreateSubscription } from "../../api/api";
import { useNotifications } from "../../components/notifications/notification";
import dayjs from "dayjs";
import { useAuthContext } from "../../providers/auth.provider";
import { v4 as uuidv4 } from "uuid";

export default function SubscriptionsCreatePage() {
  const { id } = useParams();
  const { user } = useAuthContext();

  const plan = useMemo(() => {
    if (id && plans) {
      return plans.find((p) => p.id === +id);
    }
    return undefined;
  }, [id]);

  const navigate = useNavigate();

  const { notification, notifyError } = useNotifications();

  const [cardDetails, setCardDetails] = useState<ICardDetails>();

  const { mutateAsync, isPending } = useCreateSubscription();

  const authConsentPhrase = useMemo(() => {
    return ` I confirm that I am authorized to initiate this
                          payment and agree to be charged a recurring monthly
                          subscription fee. I acknowledge that payments are
                          final and non-refundable except as outlined in the
                          refund policy.`;
  }, []);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      phone: "",
      street1: "",
      city: "",
      state: "",
      countryCode: "",
      zipCode: "",
      terms1: false,
    },
    initialTouched: {
      countryCode: true,
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Please enter first name"),
      lastName: Yup.string().required("Please enter last name"),
      phone: Yup.string().required("Please enter phone"),
      terms1: Yup.boolean().oneOf([true], "Please accept terms"),
      street1: Yup.string().required("Please enter street"),
      city: Yup.string().required("Please enter city"),
      state: Yup.string().required("Please select state"),
      countryCode: Yup.string().required("Please select country"),
      zipCode: Yup.number()
        .typeError("Please enter valid zipcode")
        .required("Please enter zipcode"),
    }),
    onSubmit: (values) => {
      if (!plan) {
        notifyError("Please select a plan.");
        return;
      }
      if (!cardDetails) {
        notifyError("Please enter payment details");
        return;
      }
      if (!cardDetails.cardToken) {
        notifyError("Please enter card number");
        return;
      }
      if (!cardDetails.expiry) {
        notifyError("Please enter card expiry");
        return;
      }

      if (values.phone.length !== 10) {
        notifyError("Please correct phone number");
        return;
      }

      mutateAsync({
        amount: plan!.price!,
        authConsent: authConsentPhrase,
        cardExpiry: cardDetails.expiry,
        cardToken: cardDetails!.cardToken!,
        city: values.city,
        countryCode: values.countryCode as any,
        firstName: values.firstName,
        lastName: values.lastName,
        monthlyCycleDate: dayjs().format("YYYY-MM-DD"),
        phone: `+1${values.phone}`,
        state: values.state,
        street1: values.street1,
        subscriptionPlanId: `${plan.id}`,
        subscriptionPlanName: plan.name,
        tax: values.state === "TX" ? 8.5 : 0,
        total: grossTotal,
        zipCode: values.zipCode,
        email: user!.useremail,
        nonce: uuidv4(),
      })
        .then(() => navigate(`/subscriptions/success`))
        .catch((err) =>
          notifyError("Whoops! Payment failed. Please try different card."),
        );
    },
  });

  const taxPercent = useMemo(() => {
    if (formik.values.state === "TX") {
      return 8.5;
    }
    return 0;
  }, [formik.values.state]);

  const taxTotal = useMemo(() => {
    if (taxPercent && plan) {
      return toFixed((taxPercent / 100) * plan.price);
    }
    return 0;
  }, [taxPercent, plan]);

  const grossTotal = useMemo(() => {
    if (plan) {
      if (taxTotal) {
        return toFixed(taxTotal + plan.price);
      }
      return plan.price;
    }
    return 0;
  }, [taxTotal, plan]);

  return (
    <div className="mx-auto max-w-5xl min-h-screen my-28">
      {notification}
      <div className="flex justify-center flex flex-col gap-2 items-center">
        <h1 className="font-bold text-2xl">Complete Subscription</h1>
        <span className="text-sm text-gray-500">
          Billed monthly. Cancel anytime.
        </span>
      </div>
      <section className="pb-4">
        <Button type="link" onClick={() => navigate(-1)}>
          Back
        </Button>

        {!plan && (
          <div className="bg-white border border-color-400 rounded-2xl px-5 py-4 mb-4 shadow-sm">
            Sorry! This plan is no longer available.
            <Button type="link" onClick={() => navigate("/home")}>
              Home
            </Button>
          </div>
        )}

        {plan && (
          <div className="my-6">
            <div className="bg-white border rounded-2xl px-5 py-4 mb-4 shadow-sm">
              <div className="flex justify-between items-start mb-2">
                <h2 className="font-semibold text-lg text-gray-800">
                  {plan?.name}
                </h2>
                <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                  Selected Plan
                </span>
              </div>

              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-gray-900">
                  ${plan?.price}
                </span>
                <span className="text-gray-500 text-sm">/ {plan?.period}</span>
              </div>

              <p className="text-sm text-gray-500 mt-2">
                Billed monthly. Cancel anytime.
              </p>
            </div>

            <form
              className="border border-gray-300 rounded-xl px-8 py-8 mb-10"
              onSubmit={formik.handleSubmit}
            >
              <h2 className="font-semibold text-lg items-center flex justify-center">
                Please Fill Your Details
              </h2>

              <div className="flex flex-col gap-2 my-2">
                <strong className="font-semibold text-sm">Country: </strong>
                <select
                  className="border rounded-lg px-2 py-2 w-full text-sm"
                  value={formik.values.countryCode}
                  onChange={(e) =>
                    formik.setFieldValue("countryCode", e.target.value)
                  }
                >
                  <option value="">Select Country</option>
                  <option value={COUNTRY_CODE.USA}>United States</option>
                  <option value={COUNTRY_CODE.AUSTRALIA}>Australia</option>
                  <option value={COUNTRY_CODE.BRAZIL}>Brazil</option>
                  <option value={COUNTRY_CODE.CANADA}>Canada</option>
                  <option value={COUNTRY_CODE.FRANCE}>France</option>
                  <option value={COUNTRY_CODE.INDIA}>India</option>
                  <option value={COUNTRY_CODE.MEXICO}>Mexico</option>
                  <option value={COUNTRY_CODE.SINGAPORE}>Singapore</option>
                  <option value={COUNTRY_CODE.UK}>United Kingdom</option>
                </select>

                {!!formik.errors.countryCode && (
                  <span className="text-red-500 text-xs">
                    Please select country
                  </span>
                )}
              </div>

              {formik.values.countryCode === "USA" && (
                <div className="my-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="flex flex-col gap-3">
                      <strong className="text-lg">Billing Details</strong>

                      <div className="grid grid-cols-2 gap-5">
                        <div className="flex flex-col gap-2 text-sm">
                          <strong className="font-semibold">
                            First Name:{" "}
                          </strong>
                          <Input
                            placeholder="First Name.."
                            className={`${formik.errors.firstName ? "border-red-300" : ""}`}
                            {...formik.getFieldProps("firstName")}
                          />
                          {!!formik.errors.firstName && (
                            <span className="text-red-500 text-xs">
                              {formik.errors.firstName}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-col gap-2 text-sm">
                          <strong className="font-semibold">Last Name: </strong>
                          <Input
                            placeholder="Last Name.."
                            className={`${formik.errors.lastName ? "border-red-300" : ""}`}
                            {...formik.getFieldProps("lastName")}
                          />
                          {!!formik.errors.lastName && (
                            <span className="text-red-500 text-xs">
                              {formik.errors.lastName}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 text-sm">
                        <strong className="font-semibold">
                          Street Address:{" "}
                        </strong>
                        <Input
                          placeholder="eg. 125 Main St."
                          className={`${formik.errors.street1 ? "border-red-300" : ""}`}
                          {...formik.getFieldProps("street1")}
                        />
                        {!!formik.errors.street1 && (
                          <span className="text-red-500 text-xs">
                            {formik.errors.street1}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col gap-2 text-sm">
                        <strong className="font-semibold">City: </strong>
                        <Input
                          placeholder="eg. NewYork"
                          className={`${formik.errors.street1 ? "border-red-300" : ""}`}
                          {...formik.getFieldProps("city")}
                        />
                        {!!formik.errors.city && (
                          <span className="text-red-500 text-xs">
                            {formik.errors.city}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col gap-2 text-sm">
                        <strong className="font-semibold text-sm">
                          State:{" "}
                        </strong>
                        <select
                          className="border rounded-lg px-3 py-2 w-full text-sm"
                          onChange={(e) =>
                            formik.setFieldValue("state", e.target.value)
                          }
                        >
                          <option value="">Select a state</option>
                          <option value="AL">Alabama</option>
                          <option value="AK">Alaska</option>
                          <option value="AZ">Arizona</option>
                          <option value="AR">Arkansas</option>
                          <option value="CA">California</option>
                          <option value="CO">Colorado</option>
                          <option value="CT">Connecticut</option>
                          <option value="DE">Delaware</option>
                          <option value="FL">Florida</option>
                          <option value="GA">Georgia</option>
                          <option value="HI">Hawaii</option>
                          <option value="ID">Idaho</option>
                          <option value="IL">Illinois</option>
                          <option value="IN">Indiana</option>
                          <option value="IA">Iowa</option>
                          <option value="KS">Kansas</option>
                          <option value="KY">Kentucky</option>
                          <option value="LA">Louisiana</option>
                          <option value="ME">Maine</option>
                          <option value="MD">Maryland</option>
                          <option value="MA">Massachusetts</option>
                          <option value="MI">Michigan</option>
                          <option value="MN">Minnesota</option>
                          <option value="MS">Mississippi</option>
                          <option value="MO">Missouri</option>
                          <option value="MT">Montana</option>
                          <option value="NE">Nebraska</option>
                          <option value="NV">Nevada</option>
                          <option value="NH">New Hampshire</option>
                          <option value="NJ">New Jersey</option>
                          <option value="NM">New Mexico</option>
                          <option value="NY">New York</option>
                          <option value="NC">North Carolina</option>
                          <option value="ND">North Dakota</option>
                          <option value="OH">Ohio</option>
                          <option value="OK">Oklahoma</option>
                          <option value="OR">Oregon</option>
                          <option value="PA">Pennsylvania</option>
                          <option value="RI">Rhode Island</option>
                          <option value="SC">South Carolina</option>
                          <option value="SD">South Dakota</option>
                          <option value="TN">Tennessee</option>
                          <option value="TX">Texas</option>
                          <option value="UT">Utah</option>
                          <option value="VT">Vermont</option>
                          <option value="VA">Virginia</option>
                          <option value="WA">Washington</option>
                          <option value="WV">West Virginia</option>
                          <option value="WI">Wisconsin</option>
                          <option value="WY">Wyoming</option>
                        </select>

                        {!!formik.errors.state && (
                          <span className="text-red-500 text-xs">
                            Please select state
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col gap-2 text-sm">
                        <strong className="font-semibold">Zip Code: </strong>
                        <Input
                          placeholder="Zipcode"
                          className={`${formik.errors.zipCode ? "border-red-300" : ""}`}
                          {...formik.getFieldProps("zipCode")}
                        />
                      </div>

                      <div className="flex flex-col gap-2 text-sm">
                        <strong className="font-semibold">Phone: </strong>
                        <Input
                          prefix="+1"
                          className={`${formik.errors.phone ? "border-red-300" : ""}`}
                          {...formik.getFieldProps("phone")}
                        />
                      </div>

                      <div className="flex flex-row items-start mt-4 mb-4">
                        <input
                          type="checkbox"
                          style={{ transform: "scale(1.3)" }}
                          id="accept-terms1"
                          className={`ml-1 mr-2 border mt-1 ${!!formik.errors.terms1 ? "border-red-400" : ""}`}
                          {...formik.getFieldProps("terms1")}
                        />
                        <p className="text-gray-700 text-sm">
                          {authConsentPhrase}
                        </p>
                      </div>

                      <div className="my-4 flex flex-col gap-3">
                        <div className="flex font-bold text-sm">
                          <span className="w-[100px]">Net Amount:</span>
                          <span>${plan.price}</span>
                        </div>

                        <div className="flex font-bold text-sm">
                          <span className="w-[100px]">
                            Tax ({taxPercent}%):
                          </span>
                          <span>${taxTotal}</span>
                        </div>

                        <div className="flex font-bold text-sm">
                          <span className="w-[100px]">Total:</span>
                          <span>${toFixedDisplay(grossTotal)}</span>
                        </div>
                      </div>

                      <div className="my-4">
                        <Button
                          disabled={
                            Object.keys(formik.errors).length > 0 ||
                            !cardDetails
                          }
                          loading={isPending}
                          type="primary"
                          onClick={formik.submitForm}
                          className="w-full"
                        >
                          Pay ${toFixedDisplay(grossTotal)}
                        </Button>
                      </div>
                    </div>

                    <div>
                      <CardPaymentForm
                        onError={(msg) => {}}
                        onSuccess={(cardDetails) => setCardDetails(cardDetails)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {formik.values.countryCode &&
                formik.values.countryCode !== "USA" && (
                  <div className="flex flex-col gap-2 my-10 px-2">
                    <span className="text-red-600 text-sm bg-red-50 px-4 py-2 border-red-300 border rounded-md">
                      Sorry! Subscriptions are available only for{" "}
                      <strong>US </strong>
                      customers.
                    </span>

                    <span className="text-green-600 text-sm bg-green-50 px-4 py-2 border rounded-md my-4">
                      Good News! You can use the freeium features for unlimited
                      time.
                    </span>
                  </div>
                )}
            </form>
          </div>
        )}
      </section>
    </div>
  );
}
